import * as React from 'react';

import { FieldValues, UseControllerProps, useWatch } from 'react-hook-form';
import { Autocomplete, TextField } from '@mui/material';
import { SyntheticEvent } from 'react';

import _startCase from 'lodash/startCase';

import { CurrentAircraft } from '../core/api/clients';
import { useFormUpdateProxy } from '../core/context/FormUpdateProxyContext';
import useEditableController from '../core/api/hooks/common/useEditableController';

import ReadOnlyField from './ReadOnlyField';
import EditableField from './EditableField';

// eslint-disable-next-line max-len
interface IControlledAircraftSelect<TFieldValues extends FieldValues> extends UseControllerProps<TFieldValues> {
  label?: string,
  readOnly?: boolean,
  options: CurrentAircraft[],
  disableEditPopper?: boolean,
}

function ControlledAircraftSelect<TFieldValues extends FieldValues>({
  control,
  label,
  name,
  readOnly,
  rules,
  options,
  disableEditPopper,
} : IControlledAircraftSelect<TFieldValues>) {
  const { requestUpdate } = useFormUpdateProxy();
  const {
    field,
    fieldState,
    revertChanges,
    commitChanges,
  } = useEditableController<TFieldValues>({
    name,
    control,
  });

  const [value, setValue] = React.useState<CurrentAircraft | null>(null);
  const [inputValue, setInputValue] = React.useState<string>('');

  const acRegistration = useWatch({ control, name });

  // eslint-disable-next-line max-len
  const isAircraft = (o: any): o is CurrentAircraft => (o as CurrentAircraft).acregistration !== undefined;

  React.useEffect(() => {
    const aircraft = options.find((o: CurrentAircraft) => o.acregistration === acRegistration);
    if (aircraft) {
      setValue(aircraft);
    }
  }, [acRegistration, options]);

  return (
    <EditableField
      fieldName={name}
      renderMode={readOnly ? 'read' : 'edit'}
      saveSuccess={() => {
        commitChanges();
        requestUpdate();
      }}
      onEditCancel={() => revertChanges()}
      disableEditPopper={disableEditPopper}
      renderEditField={() => (
        <Autocomplete
          autoComplete
          autoSelect
          options={options}
          getOptionLabel={(o: CurrentAircraft) => `${o.acregistration}`}
          value={value}
          inputValue={inputValue}
          onChange={(e: SyntheticEvent, v: string | CurrentAircraft | null) => {
            field.onChange(v !== null && isAircraft(v) ? v.acregistration : v);
          }}
          onBlur={field.onBlur}
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue.toUpperCase());
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              required={rules?.required === true ?? false}
              label={`${label || _startCase(name)}`}
              error={fieldState.invalid}
              helperText={fieldState.invalid && fieldState.error ? fieldState.error.message : null}
            />
          )}
        />
      )}
      renderReadField={() => (
        <ReadOnlyField name={label || _startCase(name)} value={value?.acregistration} />
      )}
    />
  );
}

export default ControlledAircraftSelect;
